import { ApplicationRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { IMsfFormBuilder } from '@components/form-builder/form-builder';
import { FilterOperator } from '@app/utils/filter-operator';
import {
  Bid,
  CustomFieldViewModel,
  FieldService,
  SeasonService,
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { BidService } from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import { BidWorkTypeService } from '@6grain/masofa-monolith-api-client/dictionaries/api/bidWorkType.service';
import { Crop, FieldFilter } from '@6grain/masofa-monolith-api-client/dictionaries';
import { CreateBidFormComponent } from '@app/pages/electronic-field-map/pages/fields/passport/forms/create-bid-form/create-bid-form.component';
import { BidFormData } from '@app/pages/electronic-field-map/pages/fields/passport/forms/create-bid-form/bid-form-data';
import {
  buildBidWorkTypeFormField,
  BID_WORK_TYPE_IDS,
} from '@shared/data/bid-work-type.util';

export interface CropIdentificationCreateRequestContext {
  fieldId: string;
  cropId?: string | null;
  preferredCrop?: Crop | null;
  lat?: number | null;
  lng?: number | null;
  description?: string | null;
}

interface OpenCropIdentificationCreateRequestDeps {
  dialogService: DialogService;
  bidService: BidService;
  bidWorkTypeService: BidWorkTypeService;
  fieldService: FieldService;
  seasonService: SeasonService;
  datePipe: DatePipe;
  translate: TranslateService;
  appRef: ApplicationRef;
}

export function openCropIdentificationCreateRequestDialog(
  context: CropIdentificationCreateRequestContext,
  deps: OpenCropIdentificationCreateRequestDeps,
  onSuccess?: () => void,
): DynamicDialogRef | undefined {
  const fieldId = context.fieldId?.trim();
  if (!fieldId) {
    return undefined;
  }

  const cropHolder = { list: [] as Crop[] };
  const fields = getCreateBidFormFields(cropHolder, deps.bidWorkTypeService);
  const bid: BidFormData = {
    fieldId,
    cropId: (context.cropId ?? null) as any,
    deadlineDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    description: context.description ?? deps.translate.instant('CROP_IDENTIFICATION'),
    lat: (context.lat ?? null) as any,
    lng: (context.lng ?? null) as any,
    bidWorkType: BID_WORK_TYPE_IDS.Default,
  };

  const dialogRef = deps.dialogService.open(CreateBidFormComponent, {
    header: deps.translate.instant('Create request'),
    width: '40%',
    closable: true,
    modal: true,
    appendTo: 'body',
    duplicate: true,
    baseZIndex: 12000,
    data: {
      model: bid,
      serviceMethod: 'cropMonitoringBidCustomCreatePost',
      service: deps.bidService,
      fields,
    },
  });

  dialogRef?.onClose?.subscribe((result: {success?: boolean} | undefined) => {
    if (result?.success) {
      onSuccess?.();
    }
  });

  loadNotHarvestingCropsForField(fieldId, deps.fieldService, deps.seasonService, deps.datePipe)
    .pipe(catchError(() => of([] as Crop[])))
    .subscribe((crops) => {
      cropHolder.list = withPreferredCrop(crops, context.preferredCrop);
      deps.appRef.tick();
    });

  return dialogRef;
}

function getCreateBidFormFields(cropHolder: {list: Crop[]}, bidWorkTypeService: BidWorkTypeService): IMsfFormBuilder[] {
  return [
    {
      name: 'cropId',
      title: 'Crop',
      type: 'select',
      placeholder: 'Select crop',
      getSelectOptions: () => cropHolder.list,
    },
    {
      name: 'deadlineDate',
      title: 'Deadline',
      type: 'date',
      placeholder: 'Select date',
    },
    {
      name: 'customer',
      title: 'Customer',
      type: 'input',
    },
    {
      name: 'description',
      title: 'Description',
      type: 'textarea',
    },
    buildBidWorkTypeFormField(bidWorkTypeService),
  ];
}

function loadNotHarvestingCropsForField(
  fieldId: string,
  fieldService: FieldService,
  seasonService: SeasonService,
  datePipe: DatePipe,
): Observable<Crop[]> {
  const filters: FieldFilter[] = [
    {
      filterField: 'id',
      filterOperator: FilterOperator.Equals,
      filterValue: fieldId,
    },
  ];

  return fieldService.cropMonitoringFieldCustomGetByQueryPost({ filters }).pipe(
    take(1),
    switchMap((fields) => {
      if (!fields?.length) {
        return of([] as Crop[]);
      }
      const customField = fields[0] as CustomFieldViewModel;
      const currentDate = datePipe.transform(new Date(), 'yyyy-MM-dd') as string;
      return seasonService.cropMonitoringSeasonGetByQueryPost({
        filters: [
          {
            filterField: 'fieldId',
            filterOperator: FilterOperator.Equals,
            filterValue: fieldId,
          },
          {
            filterField: 'PlantingDate',
            filterOperator: FilterOperator.LessThanOrEqual,
            filterValue: currentDate,
          },
        ],
      }).pipe(
        take(1),
        map((seasons) => {
          const cropIds = seasons?.map((s) => s.cropId) ?? [];
          const out: Crop[] = [];
          (customField?.crops as Crop[])?.forEach((crop) => {
            if (cropIds.includes(crop.id)) {
              out.push(crop);
            }
          });
          return out;
        }),
      );
    }),
  );
}

function withPreferredCrop(crops: Crop[], preferredCrop?: Crop | null): Crop[] {
  if (!preferredCrop?.id) {
    return [...crops];
  }

  if (crops.some((crop) => crop.id === preferredCrop.id)) {
    return [...crops];
  }

  return [preferredCrop, ...crops];
}
